import { SERVICE_RECEIPT_GRACE_MS } from './paymentFlow'
import type { AqsiSale } from './types'

/**
 * «Быстрая оплата» (решение владельца 24.10.2026, RP-SALE-FAST шаг 9): после подтверждённой оплаты картой продажа откладывается на
 * сервере, чек печатается в фоне, а кассир сразу набирает следующую продажу. Касса не примет новую операцию, пока чек не готов, поэтому
 * следующая оплата ждёт его конца; проблемный чек не ждётся, а требует решения.
 */

/** Сколько панель «Чек» держит строку «Чек пробит», если её не закрыли кнопкой «Понятно». */
export const FAST_DONE_SHOWN_MS = 5_000

/** «7 с», «1:05»: сколько печатается чек. */
export function printingElapsedText(since: string, now: number): string | null {
  const start = Date.parse(since)
  if (Number.isNaN(start)) return null
  const seconds = Math.max(0, Math.floor((now - start) / 1000))
  return seconds < 60 ? `${seconds} с` : `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

/** Стадия чека продажи, отложенной в фон. */
export type BackgroundReceiptStage = 'printing' | 'finishing' | 'problem'

/**
 * Чек в работе у кассы — то же правило, что у защиты на сервере (resource-aqsi 0.111.0): команда чека живая, пока операция не разрешена и не вышел её
 * срок (`deadline`). `finishing` — кабинет уже сказал `Finishing`: оплату это не задерживает. Срок вышел или операция уже разрешена, а продажа всё ещё «в
 * работе», — это обычный долг (`problem`), оплату он не закрывает. Операции в ответе нет или срока в ней нет — судить не по чему, чек считается печатающимся.
 */
function liveReceiptStage(sale: AqsiSale, now: number): BackgroundReceiptStage {
  const operation = sale.currentOperation
  if (operation === undefined || operation === null || (operation.kind !== 'receipt' && operation.kind !== 'refundReceipt')) return 'printing'
  const deadline = operation.deadline === null ? Number.NaN : Date.parse(operation.deadline)
  if (Number.isNaN(deadline)) return 'printing'
  const unresolved = operation.state === 'Pending' || operation.state === 'Sent' || operation.state === 'Unknown'
  if (!unresolved || now >= deadline) return 'problem'
  return operation.providerStatus === 'Finishing' ? 'finishing' : 'printing'
}

/**
 * Чек отложенной продажи с взятыми деньгами: печатается (`printing`) или с ним проблема (`problem`, нужен человек). Те же признаки, что у
 * этапов окна оплаты (`paymentStageOf`): чек у кассы идёт; служба ещё не отправила его только первые секунды; отказ, неизвестный исход и
 * предупреждения службы — проблема. `null` — это не фоновый чек (деньги не взяты, продажа не отложена, чек уже пробит).
 */
export function backgroundReceiptStage(sale: AqsiSale, now: number): BackgroundReceiptStage | null {
  if (!sale.isParked || sale.isFinished || sale.paymentKind !== 'Card' || sale.payment !== 'Succeeded') return null
  if (sale.fiscal === 'Succeeded' || sale.fiscal === 'IssuedOutside') return null
  if (sale.fiscal === 'Failed' || sale.fiscal === 'Unknown' || sale.attention.length > 0) return 'problem'
  if (sale.fiscal === 'InProgress') return liveReceiptStage(sale, now)
  const paidAt = Date.parse(sale.updatedAt)
  return !Number.isNaN(paidAt) && now - paidAt < SERVICE_RECEIPT_GRACE_MS ? 'printing' : 'problem'
}

/** Сколько фоновых чеков кассы печатается и сколько с проблемой. */
export interface ReceiptsAhead {
  printing: number
  problem: number
}

const NOTHING_AHEAD: ReceiptsAhead = { printing: 0, problem: 0 }

export function receiptsAheadOf(rows: readonly AqsiSale[] | undefined, now: number): ReceiptsAhead {
  if (rows === undefined) return NOTHING_AHEAD
  let printing = 0
  let problem = 0
  for (const row of rows) {
    const stage = backgroundReceiptStage(row, now)
    if (stage === 'printing') printing += 1
    else if (stage === 'problem') problem += 1
  }
  return printing === 0 && problem === 0 ? NOTHING_AHEAD : { printing, problem }
}

/** Условия, при которых оплаченная картой продажа уходит в фон: деньги взяты, чек у кассы, команда чека отправлена, продажа не отложена. */
export function canHandOffToBackground(sale: AqsiSale): boolean {
  return (
    !sale.isFinished &&
    !sale.isParked &&
    sale.paymentKind === 'Card' &&
    sale.payment === 'Succeeded' &&
    sale.fiscal === 'InProgress' &&
    sale.receiptOperationId !== null
  )
}
